/**
 * Calculate shooting statistics from an array of shots
 * @param {Array} shots - Array of shot objects
 * @param {Object} options - Optional filtering options
 * @param {string} options.team - Team identifier (currently 'left'/'right', future: teamID)
 * @returns {Object} Statistics object with FG, 3P, and FT percentages and totals
 */
export function calculateShootingStats(shots, options = {}) {
	if (!shots || shots.length === 0) {
		return {
			FGPercentage: 0,
			FGMade: 0,
			FGTotal: 0,
			TPPercentage: 0,
			TPMade: 0,
			TPTotal: 0,
			FTPercentage: 0,
			FTMade: 0,
			FTTotal: 0,
		};
	}

	// Filter by team if specified
	// Currently uses left_or_right, future: will use teamID
	let filteredShots = shots;
	if (options.team) {
		filteredShots = shots.filter(
			(shot) =>
				shot.left_or_right === options.team || shot.teamID === options.team,
		);
	}

	// Field Goals (2-pointers)
	const FGMade = filteredShots.filter(
		(shot) =>
			shot.field_goal_position === "2PFG" && shot.field_goal_result === "score",
	).length;
	const FGTotal = filteredShots.filter(
		(shot) => shot.field_goal_position === "2PFG",
	).length;

	// Three Pointers
	const TPMade = filteredShots.filter(
		(shot) =>
			shot.field_goal_position === "3PFG" && shot.field_goal_result === "score",
	).length;
	const TPTotal = filteredShots.filter(
		(shot) => shot.field_goal_position === "3PFG",
	).length;

	// Free Throws
	const FTMade = filteredShots.filter(
		(shot) =>
			shot.field_goal_position === "FT" && shot.field_goal_result === "score",
	).length;
	const FTTotal = filteredShots.filter(
		(shot) => shot.field_goal_position === "FT",
	).length;

	return {
		FGPercentage: FGTotal > 0 ? Math.round((FGMade / FGTotal) * 100) : 0,
		FGMade,
		FGTotal,
		TPPercentage: TPTotal > 0 ? Math.round((TPMade / TPTotal) * 100) : 0,
		TPMade,
		TPTotal,
		FTPercentage: FTTotal > 0 ? Math.round((FTMade / FTTotal) * 100) : 0,
		FTMade,
		FTTotal,
	};
}

/**
 * Filter shots by team
 * @param {Array} shots - Array of shot objects
 * @param {string} teamFilter - Team identifier (currently 'left'/'right', future: teamID)
 * @returns {Array} Filtered shots
 */
export function filterShotsByTeam(shots, teamFilter) {
	if (!shots || !teamFilter) return shots || [];

	// Support both current (left_or_right) and future (teamID) field names
	return shots.filter(
		(shot) => shot.left_or_right === teamFilter || shot.teamID === teamFilter,
	);
}

/**
 * Validate and process raw shot data
 * Filters out invalid shots and prepares data for visualization
 * @param {Array} rawShots - Raw shot data from game results
 * @param {Function} pixelToCourtFn - Function to convert pixel coordinates to court coordinates
 * @param {Object} options - Processing options
 * @param {number} options.minHomographyConfidence - Minimum homography confidence (default: 0.5)
 * @param {Array} options._resolution - Video resolution [width, height] (default: [1920, 1080])
 * @returns {Array} Processed and validated shots
 */
export function processRawShots(rawShots, pixelToCourtFn, options = {}) {
	const { minHomographyConfidence = 0.5 } = options;

	if (!rawShots || !Array.isArray(rawShots)) return [];

	return rawShots
		.filter((shot) => {
			// Validate shot has required fields
			if (!shot.top_field_goal_coord) return false;
			if (shot.top_field_goal_coord === "idle") return false;

			// Check homography confidence
			if (shot.homography_cc < minHomographyConfidence) return false;

			return true;
		})
		.map((shot) => {
			const courtLocation = pixelToCourtFn(
				shot.top_field_goal_coord[0],
				shot.top_field_goal_coord[1],
				shot.left_or_right,
			);

			return {
				...shot,
				location: courtLocation,
				left_or_right: shot.left_or_right,
			};
		});
}
